import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, errMsg, fmtDate, fmtNum } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";

const tone = (c) => (c.no_data ? "bg-red-50 text-red-700" : c.count === 0 ? "bg-emerald-50 text-emerald-700" : c.count < 100 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700");

const Cell = ({ c, bold, testid }) => (
  <td className="p-1">
    <Link to={c.to} data-testid={testid} className={`block rounded-lg px-3 py-2 text-right font-mono tabular-nums transition-[filter,transform] duration-150 hover:-translate-y-0.5 hover:brightness-95 ${tone(c)} ${bold ? "font-extrabold ring-1 ring-inset ring-current" : ""}`} title={bold ? "Highest in this row" : undefined}>
      {c.no_data ? "No data" : c.count === 0 ? "Done" : fmtNum(c.count)}
    </Link>
  </td>
);

const PlantChips = ({ plants, all, onChange }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="compare-plant-chips">
    {plants.map((p) => (
      <span key={p} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-sm" data-testid={`compare-chip-${p}`}>
        Plant {p}
        {plants.length > 1 && <button onClick={() => onChange(plants.filter((x) => x !== p))} data-testid={`compare-remove-${p}`} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-3 w-3" /></button>}
      </span>
    ))}
    {plants.length < 5 && (
      <Select value="" onValueChange={(v) => onChange([...plants, v])}>
        <SelectTrigger className="h-8 w-[150px] bg-white" data-testid="compare-add-plant"><Plus className="mr-1 h-3.5 w-3.5" /><SelectValue placeholder="Add plant" /></SelectTrigger>
        <SelectContent>
          {all.filter((p) => !plants.includes(p.code)).map((p) => <SelectItem key={p.code} value={p.code} data-testid={`compare-add-option-${p.code}`}>Plant {p.code}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
  </div>
);

export const PlantCompare = ({ stock }) => {
  const { plants: all } = usePlant();
  const [sel, setSel] = useState(null);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = useCallback((extra = {}) => ({ ...(sel ? { plants: sel.join(",") } : {}), ...(stock ? { stock } : {}), ...extra }), [sel, stock]);
  useEffect(() => {
    setBusy(true);
    api.get("/dashboard/compare", { params: q() }).then((r) => { setD(r.data); if (!sel) setSel(r.data.plants); })
      .catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const xlsx = () => download(`/dashboard/compare?${new URLSearchParams(q({ format: "xlsx" }))}`, `plant_comparison_${(d?.plants || []).join("-")}.xlsx`).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="plant-compare">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <PlantChips plants={sel || []} all={all} onChange={setSel} />
        <Button variant="outline" size="sm" onClick={xlsx} disabled={!d} data-testid="compare-export-xlsx"><FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />Export to Excel</Button>
      </div>
      <p className="mb-3 text-xs text-slate-500" data-testid="compare-as-at">{d ? `As at ${fmtDate(d.as_at, true)} · Basis: ${d.basis} · Total = sum of the plant columns` : "Loading…"}{busy && d && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}</p>
      {d && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="w-8 px-3 py-2">#</th><th className="px-3 py-2">Action</th>{d.plants.map((p) => <th key={p} className="px-3 py-2 text-right" data-testid={`compare-col-${p}`}>Plant {p}</th>)}<th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Gap</th></tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={r.key} className="border-t border-slate-100" data-testid={`compare-row-${r.key}`}>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2"><p className="font-medium text-slate-900">{r.label}</p><p className="text-[11px] uppercase tracking-wider text-slate-400">Owner: {r.owner}</p></td>
                  {d.plants.map((p) => <Cell key={p} c={r.cells[p]} bold={r.max_plants.includes(p)} testid={`compare-cell-${r.key}-${p}`} />)}
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold" data-testid={`compare-total-${r.key}`}>{fmtNum(r.total)}</td>
                  <td className="px-3 py-2 text-right" data-testid={`compare-gap-${r.key}`}>{r.gap ? <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">gap {fmtNum(r.gap)}</span> : <span className="text-xs text-slate-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
